import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {Card} from 'shells/card/card';
import {EmptyStateSmall} from 'shells/empty-states/empty-state-small';
import {Illustration} from 'shells/illustrations/illustration';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Link} from 'shells/link/link';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {TwoLine} from 'shells/two-line/two-line';
import {ModalBody} from 'shells/typography/modal-body';
import {UppercaseLabel} from 'shells/typography/uppercase-label';

import type {OutreachItem} from './send-marketing-outreach-modal';

import styles from './ready-for-sending-page.module.css';

type Props = {
    outreachItem: OutreachItem;
    outreachItemType: string;
    outreachItemTypeReadable: string;
    isLoading: boolean;
    error: boolean;
    recipientEntityType: string;
    numRecipients: number;
    numAlreadyReceived: number | null | undefined;
};

export function ReadyForSendingMessagePreview(props: Props) {
    const {
        outreachItem,
        outreachItemType,
        outreachItemTypeReadable,
        isLoading,
        error,
        recipientEntityType,
        numRecipients,
        numAlreadyReceived,
    } = props;

    const isDripSequence = outreachItemType === 'DRIP_SEQUENCE';

    const linkToOutreachItem =
        outreachItem.__typename === 'Edition' && outreachItem.campaign.type === 'NEWSLETTER'
            ? outreachItem.campaign.htmlUrlPath
            : outreachItem.htmlUrlPath;

    const linkDisplayText =
        outreachItem.__typename === 'Edition' && outreachItem.campaign.type === 'NEWSLETTER'
            ? outreachItem.campaign.name
            : outreachItem.name;

    if (isLoading) {
        return <LoadingPage />;
    }

    if (error) {
        return (
            <EmptyStateSmall
                title='An unknown error occurred'
                subtitle={
                    <div className='flex'>
                        Please try again or&nbsp;
                        <Link href='https://www.nutshell.com/support' variant='primary'>
                            contact support
                        </Link>
                        .
                    </div>
                }
                icon={
                    <div style={{width: '150px'}}>
                        <Illustration src='plug-error-state' width='100%' height='100%' />
                    </div>
                }
            />
        );
    }

    return (
        <div className={styles['ready-for-sending-message-preview']}>
            <div>
                <DetailSectionItem
                    title={outreachItemTypeReadable}
                    isTitleLabel={true}
                    content={
                        <>
                            <div>
                                <Link
                                    to={linkToOutreachItem}
                                    target='_blank'
                                    // @ts-expect-error
                                    as={RoutedLink}
                                    size='big'
                                    variant='primary'
                                >
                                    {linkDisplayText}
                                </Link>
                            </div>
                            {outreachItemType === 'NEWSLETTER' ? (
                                <div className={styles['light-text']}>{outreachItem.name}</div>
                            ) : undefined}
                        </>
                    }
                />
                <DetailSectionItem
                    title={isDripSequence ? 'Messages' : undefined}
                    isTitleLabel={isDripSequence}
                    content={<MessagePreviewContent outreachItem={outreachItem} />}
                />
            </div>
            <div>
                <DetailSectionItem
                    title='From'
                    content={
                        outreachItem.from
                            ? outreachItem.from.emailAddress.formatted
                            : 'Sender email'
                    }
                />
                <DetailSectionItem
                    title={isDripSequence ? 'Adding' : 'To'}
                    content={
                        <SendingToSection
                            recipientEntityType={recipientEntityType}
                            isDripSequence={isDripSequence}
                            numAlreadyReceived={numAlreadyReceived}
                            numRecipients={numRecipients}
                        />
                    }
                />
                {outreachItem.__typename === 'Edition' ? (
                    <>
                        <DetailSectionItem
                            title='Subject'
                            content={
                                outreachItem.emailTemplate
                                    ? outreachItem.emailTemplate.subject
                                    : 'No subject' // Should never happen
                            }
                        />
                        <DetailSectionItem
                            title='Preview text'
                            content={
                                outreachItem.emailTemplate &&
                                outreachItem.emailTemplate.previewText ? (
                                    outreachItem.emailTemplate.previewText
                                ) : (
                                    <span className={styles['light-text']}>
                                        <i>None</i>
                                    </span>
                                )
                            }
                        />
                    </>
                ) : undefined}
            </div>
        </div>
    );
}

function DetailSectionItem(props: {
    title: string | null | undefined;
    isTitleLabel?: boolean;
    content: React.ReactElement<any> | null | undefined | string;
}) {
    const sectionTitle = <span className={styles['dark-text']}>{props.title}</span>;

    return (
        <div className={styles['detail-section-item']}>
            <div>
                {props.isTitleLabel ? (
                    <UppercaseLabel>{sectionTitle}</UppercaseLabel>
                ) : (
                    <ModalBody>{sectionTitle}</ModalBody>
                )}
            </div>
            {typeof props.content === 'string' ? <div>{props.content}</div> : props.content}
        </div>
    );
}

function MessagePreviewContent(props: {outreachItem: OutreachItem}) {
    const {outreachItem} = props;
    if (outreachItem.__typename === 'Edition') {
        return (
            <div className={styles['message-thumbnail-container']}>
                <img
                    // @ts-expect-error
                    src={outreachItem.emailTemplate ? outreachItem.emailTemplate.thumbnailUrl : ''}
                    alt={outreachItem.name}
                />
            </div>
        );
    } else {
        // @ts-expect-error
        const messages = [];
        let additionalMessages = 0;

        outreachItem.editions.forEach((edition, index) => {
            // We'll show all 3 messages if there's ~exactly~ 3
            if (index < 2 || outreachItem.editions.length === 3) {
                messages.push({
                    id: edition.id,
                    name: edition.name,
                    // Should literally never happen that there is no subject but...
                    subject: edition.emailTemplate ? edition.emailTemplate.subject : 'No subject',
                });
            } else {
                additionalMessages++;
            }
        });

        return (
            <StackLegacy spacing={8}>
                {/* @ts-expect-error */}
                {messages.map((message) => (
                    <Card hasBorder={true} key={message.id}>
                        <div className={styles['drip-sequence-message-card']}>
                            <TwoLine topRow={message.name} bottomRow={message.subject} />
                        </div>
                    </Card>
                ))}
                {additionalMessages ? (
                    <Card hasBorder={true}>
                        <div className={styles['drip-sequence-message-card']}>
                            <i className={styles['light-text']}>+ {additionalMessages} more</i>
                        </div>
                    </Card>
                ) : undefined}
            </StackLegacy>
        );
    }
}

function SendingToSection(props: {
    recipientEntityType: string; // Is passed in correct form (singular or plural),
    isDripSequence: boolean;
    numRecipients: number;
    numAlreadyReceived: number | null | undefined;
}) {
    const {recipientEntityType, isDripSequence, numRecipients, numAlreadyReceived} = props;

    return (
        <>
            <div>Custom segment</div>
            <div className={styles['light-text']}>
                {numRecipients} recipient{numRecipients === 1 ? '' : 's'}
            </div>
            {numAlreadyReceived ? (
                <div className={styles['warning-text']}>
                    {numAlreadyReceived} {recipientEntityType}{' '}
                    {numAlreadyReceived === 1 ? 'has' : 'have'} already received this{' '}
                    {isDripSequence ? 'drip sequence' : 'message'}
                </div>
            ) : undefined}
        </>
    );
}
